import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { isValidNumber } from '../../../../../utils/math/number';
import { Icon } from '../../../icon/icon';

/**
 * What the control under the pointer does, shown in the band at the bottom of the snapping dialog
 * instead of a tooltip, so that it stays within the dialog, also in a window of its own, and never
 * covers the field below it.
 */
export type SnappingHelp = { title: React.ReactNode; text?: React.ReactNode };

/** Shows the help of a control in the snapping dialog, or none. Undefined outside the dialog. */
export const SnappingHelpContext = React.createContext<
  ((help: SnappingHelp | undefined) => void) | undefined
>(undefined);

/**
 * The props that show the help in the band while the pointer is over the element or it has focus.
 */
export const useHelp = (help: SnappingHelp | undefined) => {
  const setHelp = React.useContext(SnappingHelpContext);

  if (!setHelp || !help) {
    return {};
  }

  const show = () => setHelp(help);
  const hide = () => setHelp(undefined);
  return { onMouseEnter: show, onMouseLeave: hide, onFocus: show, onBlur: hide };
};

/**
 * The title of a section of the sheet, e.g. Snappa mot, with its info icon, whose help is shown in
 * the band.
 */
export const SheetHeader = (props: { title: string; info?: React.ReactNode }) => {
  const help = useHelp(props.info ? { title: props.title, text: props.info } : undefined);

  return (
    <Header data-sheet-header={true}>
      {props.title}
      {props.info && (
        <InfoIcon
          aria-label={props.title}
          color="#00a9e0"
          icon={['fas', 'circle-info']}
          tabIndex={0}
          {...help}
        />
      )}
    </Header>
  );
};

/**
 * A row of the sheet: what it is in the left half and its value in the right half, so that the
 * values of all rows line up.
 */
export const SheetRow = styled.div<{ $off?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  min-height: 34px;
  padding: 0 calc(var(--padding) + 0.5em);
  // Lines between the rows only, not before the first of a section nor after the last
  border-top: 1px solid ${(props) => props.theme.color.gray.lightest};

  [data-sheet-header] + & {
    border-top: none;
  }

  ${(props) =>
    props.$off &&
    css`
      ${RowLabel} {
        color: ${props.theme.color.gray.dark};
        font-weight: 500;
      }
    `}
`;

export const RowStart = styled.span`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
`;

export const RowLabel = styled.label`
  overflow: hidden;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
`;

/** The value half of a row, the remove button or its space at its end. */
export const RowEnd = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  min-width: 0;
`;

export const Hint = styled.span`
  overflow: hidden;
  color: ${(props) => props.theme.color.gray.dark};
  font-size: 0.9em;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** The width of the remove button, kept on rows without one so that their values line up. */
export const RemoveSpace = styled.span`
  flex: none;
  width: 22px;
`;

/**
 * A number with its unit inside the field, after it. Named by its title, which the row shows.
 */
export const ValueField = (props: {
  title: string;
  unit?: string;
  defaultValue: number;
  onChange: (value: number) => void;
  /** Whether a number can be used, any number by default. */
  isValid?: (value: number) => boolean;
  autoFocus?: boolean;
}) => {
  const [text, setText] = React.useState(props.defaultValue.toString());
  const isValid = (x: number) => isValidNumber(x) && (props.isValid?.(x) ?? true);
  const invalid = !isValid(parseFloat(text));

  return (
    <Field $invalid={invalid}>
      <input
        aria-invalid={invalid}
        aria-label={props.title}
        autoFocus={props.autoFocus}
        step={0.01}
        type="number"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const value = parseFloat(e.target.value);

          if (isValid(value)) {
            props.onChange(value);
          }
        }}
        onFocus={(e) => e.target.select()}
      />
      {props.unit && <Unit>{props.unit}</Unit>}
    </Field>
  );
};

/**
 * The setting of a row, which opens its choices. Its choice is named for the screen reader and on
 * hover, and shown only where the value doesn't show it, e.g. Längd · 2D, unlike the unit of a
 * slope.
 */
export const ChoiceLabel = (props: {
  setting: string;
  /** Shown instead of the setting, e.g. Z-offset for the height at an offset. */
  label?: string;
  choice: string;
  showChoice?: boolean;
  expanded: boolean;
  help?: SnappingHelp;
  onClick: () => void;
}) => (
  <ChoiceButton
    aria-expanded={props.expanded}
    aria-label={`${props.setting} · ${props.choice}`}
    title={`${props.setting} · ${props.choice}`}
    type="button"
    onClick={props.onClick}
    {...useHelp(props.help)}
  >
    <span>{props.label ?? props.setting}</span>
    {props.showChoice && <ChoiceText>· {props.choice}</ChoiceText>}
    <Icon fixedWidth={true} icon={['fal', 'chevron-down']} />
  </ChoiceButton>
);

/**
 * A value that the choice sets and that can't be changed, e.g. the 90° of perpendicular, greyed
 * out where the field of a value would be.
 */
export const FixedValue = (props: { value: string; unit?: string }) => (
  <Fixed>
    {props.value}
    {props.unit && <Unit>{props.unit}</Unit>}
  </Fixed>
);

export type PickerRow = {
  name: string;
  /** Whether the setting is on, with one of its choices. */
  active: boolean;
  choices: {
    title: string;
    /** Shown on its button instead of the title, which names it, e.g. the symbol of a slope. */
    label?: string;
    active: boolean;
    help: SnappingHelp;
    onPick: () => void;
  }[];
};

/**
 * The choices of the settings, a row of buttons for each, with the help of the one under the
 * pointer below them. In the flow, under what opened it, so that it stays within the dialog. It
 * closes on Escape and on a click outside of it.
 */
export const ChoicePicker = (props: { title: string; rows: PickerRow[]; onClose: () => void }) => {
  const { t } = useTranslation();
  const ref = React.useRef<HTMLDivElement>(null);
  const [help, setHelp] = React.useState<SnappingHelp>();
  const onClose = React.useRef(props.onClose);
  onClose.current = props.onClose;

  React.useEffect(() => {
    // The document of the dialog, another window's where it is in a window of its own.
    const document = ref.current?.ownerDocument;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element;
      // Not on what opened it, which closes it on its own
      if (!ref.current?.contains(target) && !target.closest?.('[aria-expanded="true"]')) {
        onClose.current();
      }
    };

    // Also while what opened it still has the focus
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose.current();
      }
    };

    document?.addEventListener('pointerdown', onPointerDown);
    document?.addEventListener('keydown', onKeyDown);
    return () => {
      document?.removeEventListener('pointerdown', onPointerDown);
      document?.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return (
    <Picker aria-label={props.title} ref={ref} role="group">
      <PickerHeader>
        <span>{props.title}</span>
        <Icon
          aria-label={t('close', { ns: 'common' })}
          fixedWidth={true}
          icon={['fal', 'xmark']}
          onClick={props.onClose}
        />
      </PickerHeader>
      {props.rows.map((row) => (
        <PickerRowStyled $single={props.rows.length === 1} key={row.name}>
          {props.rows.length > 1 && (
            <PickerName>
              {row.name}
              {row.active && <Icon fixedWidth={true} icon={['fal', 'check']} />}
            </PickerName>
          )}
          <PickerChoices>
            {row.choices.map((choice) => (
              <PickerChoice
                $active={choice.active}
                aria-label={props.rows.length > 1 ? `${row.name}: ${choice.title}` : choice.title}
                aria-pressed={choice.active}
                key={choice.title}
                type="button"
                onBlur={() => setHelp(undefined)}
                onClick={choice.onPick}
                onFocus={() => setHelp(choice.help)}
                onMouseEnter={() => setHelp(choice.help)}
              >
                {choice.label ?? choice.title}
              </PickerChoice>
            ))}
          </PickerChoices>
        </PickerRowStyled>
      ))}
      <PickerHelp aria-live="polite">
        {help ? (
          <>
            <b>{help.title}.</b> {help.text}
          </>
        ) : (
          t('snappingDialog.pickerHint', { ns: 'skyviewDesign' })
        )}
      </PickerHelp>
    </Picker>
  );
};

export const RemoveButton = (props: { name: string; onClick: () => void }) => {
  const { t } = useTranslation();

  return (
    <IconButton
      aria-label={t('snappingDialog.remove', { ns: 'skyviewDesign', name: props.name })}
      type="button"
      onClick={props.onClick}
    >
      <Icon fixedWidth={true} icon={['fal', 'xmark']} />
    </IconButton>
  );
};

export const AddButton = (props: { expanded: boolean; onClick: () => void }) => {
  const { t } = useTranslation();

  return (
    <AddRow>
      <Add aria-expanded={props.expanded} type="button" onClick={props.onClick}>
        <Icon fixedWidth={true} icon={['fal', 'plus']} />
        {t('snappingDialog.add', { ns: 'skyviewDesign' })}
      </Add>
    </AddRow>
  );
};

/**
 * The snap styles at the top of the dialog, which stay there as the settings below are scrolled.
 */
export const StylesBar = styled.div.attrs({ 'data-styles-bar': true })`
  position: sticky;
  top: 0;
  z-index: 2;
  // As around the buttons of the right-side menu
  padding: 0.8em;
  background-color: white;
`;

export const EmptyHint = styled.p`
  margin: 0;
  padding: 6px calc(var(--padding) + 0.5em) 2px;
  color: ${(props) => props.theme.color.gray.dark};
  font-size: 0.9em;
  font-style: italic;
  line-height: 1.45;
`;

/**
 * The help of the control under the pointer, in the band at the bottom of the dialog.
 */
export const HelpBand = (props: { help: SnappingHelp }) => (
  <HelpComponent aria-live="polite">
    <HelpTitle>
      <Icon color="#00a9e0" fixedWidth={true} icon={['fas', 'circle-info']} />
      {props.help.title}
    </HelpTitle>
    {props.help.text && <HelpText>{props.help.text}</HelpText>}
  </HelpComponent>
);

// Set apart from the rows, so that each section stands out
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4em;
  // As far from the section above as the snap styles are from it, which have that space below
  // them already, and as far from its own rows
  margin-top: 0.8em;
  margin-bottom: 0.8em;
  padding: 6px calc(var(--padding) + 0.5em);
  // Apart from what is above it; its background sets it apart from its rows
  border-top: 1px solid ${(props) => props.theme.color.gray.light};
  background-color: #f3f4f4;
  color: ${(props) => props.theme.color.gray.dark};
  font-size: 0.8em;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;

  [data-styles-bar] + & {
    margin-top: 0;
  }
`;

const InfoIcon = styled(Icon)`
  font-size: 1.15em;
`;

// Fills the value half of its row
const Field = styled.span<{ $invalid: boolean }>`
  display: inline-flex;
  flex: 1 1 0;
  align-items: center;
  gap: 3px;
  min-width: 0;
  height: 24px;
  padding: 0 6px 0 4px;
  border: 1px solid
    ${(props) => (props.$invalid ? props.theme.color.red : props.theme.color.gray.light)};
  border-radius: 4px;
  background-color: white;

  &:focus-within {
    border-color: ${(props) => props.theme.color.blue};
    box-shadow: 0 0 0 1px ${(props) => props.theme.color.blue};
  }

  input {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    padding: 0;
    border: none;
    outline: none;
    background: none;
    color: inherit;
    font: inherit;
    text-align: right;
    font-variant-numeric: tabular-nums;
    appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      margin: 0;
      appearance: none;
    }
  }
`;

// Never narrower than its text: a longer one, e.g. in another language, reaches into the empty
// part of the left half, as the row is aligned to the end
const Fixed = styled.span`
  display: inline-flex;
  flex: 1 0 auto;
  white-space: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  min-width: 0;
  height: 24px;
  padding: 0 6px 0 4px;
  border: 1px solid ${(props) => props.theme.color.gray.lightest};
  border-radius: 4px;
  background-color: ${(props) => props.theme.color.gray.lightest};
  color: ${(props) => props.theme.color.gray.dark};
  font-variant-numeric: tabular-nums;
`;

const Unit = styled.span`
  flex: none;
  color: ${(props) => props.theme.color.gray.dark};
  font-size: 0.9em;
`;

const ChoiceButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
  max-width: 100%;
  margin-left: -3px;
  padding: 3px;
  border: none;
  border-radius: 4px;
  background: none;
  color: inherit;
  font: inherit;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  svg {
    flex: none;
    color: ${(props) => props.theme.color.gray.medium};
    font-size: 0.8em;
  }

  &:hover,
  &[aria-expanded='true'] {
    background-color: ${(props) => props.theme.color.gray.lightest};

    svg {
      color: ${(props) => props.theme.color.gray.dark};
    }
  }
`;

const ChoiceText = styled.span`
  min-width: 0;
  overflow: hidden;
  color: ${(props) => props.theme.color.gray.dark};
  font-weight: 500;
  text-overflow: ellipsis;
`;

const Picker = styled.div`
  margin: 2px 8px 6px;
  padding: 0 4px 6px;
  border: 1px solid ${(props) => props.theme.color.gray.light};
  border-radius: 6px;
  background-color: white;
  box-shadow: 0 2px 8px rgba(20, 30, 35, 0.12);
`;

const PickerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 2px 2px 6px;
  color: ${(props) => props.theme.color.gray.dark};
  font-size: 0.8em;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
`;

const PickerRowStyled = styled.div<{ $single: boolean }>`
  display: grid;
  grid-template-columns: ${(props) => (props.$single ? 'minmax(0, 1fr)' : '66px minmax(0, 1fr)')};
  gap: 6px;
  align-items: center;
  padding: 3px 4px;
`;

const PickerName = styled.span`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.9em;
  font-weight: 600;

  svg {
    color: ${(props) => props.theme.color.gray.dark};
    font-size: 0.85em;
  }
`;

const PickerChoices = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
`;

const PickerChoice = styled.button<{ $active: boolean }>`
  height: 24px;
  padding: 0 7px;
  border: 1px solid
    ${(props) => (props.$active ? props.theme.color.gray.dark : props.theme.color.gray.light)};
  border-radius: 4px;
  background-color: ${(props) => (props.$active ? props.theme.color.gray.dark : 'white')};
  color: ${(props) => (props.$active ? 'white' : 'inherit')};
  font: inherit;
  font-size: 0.88em;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: ${(props) =>
      props.$active ? props.theme.color.gray.dark : props.theme.color.gray.lightest};
  }
`;

const PickerHelp = styled.p`
  min-height: calc(3 * 1.4em);
  margin: 4px 4px 0;
  padding-top: 5px;
  border-top: 1px solid ${(props) => props.theme.color.gray.lightest};
  color: ${(props) => props.theme.color.gray.dark};
  font-size: 0.85em;
  line-height: 1.4;

  b {
    color: ${(props) => props.theme.color.gray.darkest};
  }
`;

const IconButton = styled.button`
  display: grid;
  flex: none;
  place-items: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: none;
  color: ${(props) => props.theme.color.gray.dark};
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.theme.color.gray.lightest};
    color: ${(props) => props.theme.color.red};
  }
`;

const AddRow = styled.div`
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: 0 calc(var(--padding) + 0.5em);
`;

const Add = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 11px 0 7px;
  border: 1px dashed ${(props) => props.theme.color.gray.medium};
  border-radius: 13px;
  background-color: white;
  color: ${(props) => props.theme.color.gray.dark};
  font: inherit;
  font-weight: 600;
  cursor: pointer;

  &:hover,
  &[aria-expanded='true'] {
    border-style: solid;
    background-color: ${(props) => props.theme.color.gray.lightest};
    color: ${(props) => props.theme.color.gray.darkest};
  }
`;

const HelpComponent = styled.div`
  flex: 1 0 auto;
  padding: 0.9em 1.5em 1.1em;
  border-top: 1px solid ${(props) => props.theme.color.gray.light};
  background-color: #f7f8f8;
  color: ${(props) => props.theme.color.gray.darkest};
  font-size: 9.5pt;
`;

const HelpTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4em;
  margin-bottom: 0.3em;
  font-weight: 700;
`;

const HelpText = styled.div`
  color: ${(props) => props.theme.color.gray.dark};
  line-height: 1.45;
`;
